<script setup>
  import { computed, onMounted, ref, inject, watch } from 'vue'
  import { NSelect } from 'naive-ui'
  import { extractValueOrTransform, isDexieTableExists } from '@/services/object-utils'
  import { debounce } from 'lodash'
  import { log } from '@/services/log'

  const vModelValue = defineModel('input')

  const props = defineProps({
    multiple: {
      type: Boolean,
      required: true,
      default: false,
    },
    clearable: {
      type: Boolean,
      required: false,
      default: true,
    },
    filterable: {
      type: Boolean,
      required: false,
      default: true,
    },
    placeholder: {
      type: String,
      required: true,
      default: '',
    },
    dxTableName: {
      type: String,
      required: true,
      default: '',
    },
    dxFilterFn: {
      type: Function,
      required: false,
      default: () => true,
    },
    serviceUri: {
      type: String,
      required: true,
      default: '',
    },
    optionsLabel: {
      type: [String, Function],
      required: true,
      default: '',
    },
    submitValueKey: {
      type: [String, Function],
      required: true,
      default: 'externalId',
    },
    id: {
      type: String,
      required: false,
      default: null,
    },
    afterResponse: {
      type: Function,
      required: false,
      default: (items) => {
        const result = items.filter((d) => d.status === 'ACTIVE')
        return result
      },
    },
    renderOptionFn: {
      type: Function,
      required: false,
      default: undefined,
    },
    allowAdditionalOptionInfo: {
      type: Boolean,
      required: false,
      default: false,
    },
    defaultValue: {
      required: false,
      default: null,
    },
  })
  const svc = inject('apiService')
  const dx = inject('dexie')
  const optionsData = ref([])
  const retryCount = ref(0)
  const maxRetryAttempts = 1
  const idGenerator = computed(() => {
    const uri = props.serviceUri ?? '/'
    const str = uri.startsWith('/') ? uri.substring(1) : uri
    return props.id ? props.id : `${str}ArsSmartSelect`
  })

  const extractSubmitValueKey = (item) => extractValueOrTransform(item, props.submitValueKey)

  const extractLabel = (item) => extractValueOrTransform(item, props.optionsLabel)

  const createOptions = (items) => {
    const uniqueValues = new Set(items.map(extractSubmitValueKey))
    return Array.from(uniqueValues).map((value) => {
      const item = items.find((item) => extractSubmitValueKey(item) === value)
      return props.allowAdditionalOptionInfo
        ? { ...item, label: extractLabel(item), value }
        : { label: extractLabel(item), value }
    })
  }
  const fetchOptions = async (search = '') => {
    let items = []
    const params = { externalIds: vModelValue.value, search }
    let lastSynced = null
    try {
      if (isDexieTableExists(dx, props.dxTableName)) {
        const promises = [
          svc.idbPage(params, dx, props.dxTableName, props.submitValueKey, props.optionsLabel),
          svc.idbSearch(params, dx, {
            offlineStoreName: props.dxTableName,
            keyFieldName: props.submitValueKey,
            labelFieldNameOrFunction: props.optionsLabel,
            filterFn: props.dxFilterFn,
          }),
        ]
        const idbItems = []
        const results = await Promise.all(promises)
        results.forEach((result) => {
          if (result?.items) {
            idbItems.push(...result.items)
          }
        })
        items = idbItems?.length > 0 ? idbItems : []
        lastSynced = (await dx.table(props.dxTableName)?.orderBy('lastModifiedTimestamp')?.last()) || null
      }
      if (items.length === 0 && !lastSynced) {
        items = await svc.requestGet(props.serviceUri, params)
      }
      items = props.afterResponse ? props.afterResponse(items) : items || []
      optionsData.value = items.length > 0 ? createOptions(items) : []
    } catch (error) {
      log('Failed to fetch options:', error)
    }
  }
  // TODO: There is probably a better v-model way to do this, but setting
  // vModelValue.value directly does not propagate the change to the model
  // so just create an event.
  const emit = defineEmits(['defaultValueSet'])
  const setDefaultValue = (options) => {
    const defaultValue = options.find((o) => o.label === props.defaultValue)?.value || null
    emit('defaultValueSet', defaultValue)
  }
  watch(optionsData, async (newVal) => {
    if (newVal.length > 0) {
      setDefaultValue(newVal)
      retryCount.value = 0
      return
    }
    if (retryCount.value < maxRetryAttempts) {
      log('Fetching options - attempt,newVal', newVal, retryCount.value + 1)
      await fetchOptions()
      retryCount.value++
    } else {
      log('Max retry attempts reached. No more fetch attempts for', props.serviceUri, 'data')
    }
  })
  onMounted(fetchOptions)

  const handleSearch = debounce(async (search) => fetchOptions(search), 300)
</script>

<template>
  <NSelect
    :id="idGenerator"
    v-model:value="vModelValue"
    :clearable="props.clearable"
    :placeholder="props.placeholder"
    :options="optionsData"
    :filterable="props.filterable"
    :multiple="props.multiple"
    :render-option="props.renderOptionFn"
    @search="handleSearch"
    :default-value="props.defaultValue"
    @update:value="(val) => log('ᛈᛖ‍ᛡᚩ‍ᚱᚦ ᛣᚩ‍ᚩᛏ update:value', val)"
  />
</template>
